import React, { useState, useEffect, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  FeatureGroup,
  LayersControl,
  Marker,
  Popup,
  Rectangle,
} from "react-leaflet";
import LZString from "lz-string";
import MapLegendWrapper from '../../components/MapLegendWrapper';
import { EditControl } from "react-leaflet-draw";
import { useDispatch, useSelector } from "react-redux";
import {
  addArea,
  clearAllMapState,
  deleteArea,
  setFileContent,
  setUploaderFlag,
  setShowOverlay,
} from "../../reducers/MapSlice";
import { useMap } from "react-leaflet/hooks";
import L from "leaflet";
import { useLeafletLocale } from "../../hooks/useLeafletLocale"
import markerIconPng from "leaflet/dist/images/marker-icon.png";
import { getWeatherData } from "../../api/WeatherApi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Icon } from "leaflet";
import { isMarkerWithinArabBoundries } from "../../utils/conversions";
import testImg from "./download.png";
import { CRS } from "leaflet";
import "./styles.css";
import WorkingAreaBoundary from "../../components/WorkingAreaBoundary";
import zIndex from "@mui/material/styles/zIndex";
import { url } from "../../api/axios_client";

// Component to save and restore map zoom/center
function MapStatePersistence() {
  const map = useMap();

  useEffect(() => {
    // Restore zoom and center from localStorage
    const savedZoom = localStorage.getItem('mapZoom');
    const savedCenter = localStorage.getItem('mapCenter');

    if (savedZoom && savedCenter) {
      try {
        const zoom = parseInt(savedZoom, 10);
        const center = JSON.parse(savedCenter);
        map.setView(center, zoom);
      } catch (error) {
        console.error('Error restoring map state:', error);
      }
    }

    // Save zoom and center on changes
    const handleMoveEnd = () => {
      const center = map.getCenter();
      const zoom = map.getZoom();
      localStorage.setItem('mapZoom', zoom.toString());
      localStorage.setItem('mapCenter', JSON.stringify([center.lat, center.lng]));
    };

    map.on('moveend', handleMoveEnd);
    map.on('zoomend', handleMoveEnd);

    return () => {
      map.off('moveend', handleMoveEnd);
      map.off('zoomend', handleMoveEnd);
    };
  }, [map]);

  return null;
}

// FIX: Component to restore drawn shapes from Redux - runs only once on mount
function RestoreDrawnShapes() {
  const map = useMap();
  const areas = useSelector((state) => state.mapState.areas);
  const showOverlay = useSelector((state) => state.mapState.showOverlay);

  // Run only once on mount - empty dependency array
  useEffect(() => {
    if (!areas || areas.length === 0) return;

    areas.forEach((area) => {
      // Handle uploaded GeoJSON files
      if (typeof area.coordinates === 'object' && area.coordinates.type === 'FeatureCollection') {
        try {
          const geoJsonLayer = L.geoJSON(area.coordinates, {
            style: {
              fillOpacity: showOverlay ? 0.2 : 0,
              fillColor: '#1976d2',
              weight: 2,
              color: '#1976d2'
            },
            onEachFeature: (feature, layer) => {
              if (feature.properties) {
                layer.bindPopup(
                  Object.entries(feature.properties)
                    .map(([key, value]) => `${key}: ${value}`)
                    .join('<br>')
                );
              }
            }
          });
          geoJsonLayer.addTo(map);
        } catch (error) {
          console.error('Error restoring GeoJSON:', error);
        }
        return;
      }

      // Skip invalid coordinates
      if (!Array.isArray(area.coordinates) || area.coordinates.length === 0) {
        return;
      }

      try {
        L.polygon(
          area.coordinates.map(coord => [coord[1], coord[0]]),
          {
            fillOpacity: showOverlay ? 0.2 : 0,
            fillColor: '#1976d2',
            weight: 2,
            color: '#1976d2'
          }
        ).addTo(map);
      } catch (error) {
        console.error('Error restoring shape:', error);
      }
    });
  }, []); // Empty dependency array - only run once on mount

  return null;
}

function UpdateOverlayStyle({ showOverlay, geoJsonLayerRef, areas }) {
  const map = useMap();

  useEffect(() => {
    map.eachLayer((layer) => {
      if ((layer instanceof L.Polygon || layer instanceof L.Rectangle || layer instanceof L.GeoJSON) &&
        !layer.options.className?.includes('working-area-boundary')) {
        if (typeof layer.setStyle === 'function') {
          layer.setStyle({
            fillOpacity: showOverlay ? 0.2 : 0, // only toggle fill
            fillColor: '#1976d2',
            weight: 2,
            color: '#1976d2'
          });
        }
      }
    });
  }, [map, showOverlay, geoJsonLayerRef, areas]);

  return null;
}

function Map(props) {
  const { hideButtons } = props;
  console.log("LEGEND DATA:", props.legendData)
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const markerRef = React.useRef(null);
  const center = { lat: 19.55979, lng: 29.267578 };
  const [markerPosition, setMarkerPosition] = React.useState(center);

  const areas = useSelector((state) => state.mapState.areas);
  const datePickerTimeFrom = useSelector(
    (state) => state.mapState.date.datePickerTimeFrom
  );
  const datePickerTimeTo = useSelector(
    (state) => state.mapState.date.datePickerTimeTo
  );
  const currentWeather = useSelector((state) => state.mapState.currentWeather);
  const fileContent = useSelector(
    (state) => state.mapState.uploadedFileContent
  );
  const uploaderFlag = useSelector((state) => state.mapState.uploaderFlag);
  const [deleteState, setDelete] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [geoJsonLayerRef, setGeoJsonLayerRef] = useState(null);
  const prevFileContentRef = React.useRef(null);
  const showOverlay = useSelector((state) => state.mapState.showOverlay);
  useLeafletLocale();

  // Listen for external clear event (fired by parent when "Remove Area" is in right panel)
  useEffect(() => {
    const handleClear = () => {
      deleteUploadedFiles();
    };
    window.addEventListener('clearDrawnShapes', handleClear);
    return () => window.removeEventListener('clearDrawnShapes', handleClear);
  }, []);

  // Detect new file uploads and reset geoJsonLayerRef to trigger AddGeoJson
  useEffect(() => {
    if (fileContent && typeof fileContent === 'object' && fileContent.type === 'FeatureCollection') {
      if (prevFileContentRef.current !== null) {
        const prevStr = JSON.stringify(prevFileContentRef.current);
        const currStr = JSON.stringify(fileContent);
        if (prevStr !== currStr) {
          setGeoJsonLayerRef(null);
        }
      }
      prevFileContentRef.current = fileContent;
    } else if (!fileContent || fileContent === "") {
      prevFileContentRef.current = null;
    }
  }, [fileContent]);

  const _onCreated = (e) => {
    let layer = e.layer;
    let coordinates;

    // Handle different layer types
    if (layer instanceof L.Circle) {
      const center = layer.getLatLng();
      const radius = layer.getRadius();
      const points = 64;
      coordinates = [];

      for (let i = 0; i <= points; i++) {
        const angle = (i / points) * 2 * Math.PI;
        const dx = radius * Math.cos(angle);
        const dy = radius * Math.sin(angle);

        const lat = center.lat + (dy / 111320);
        const lng = center.lng + (dx / (111320 * Math.cos(center.lat * Math.PI / 180)));

        coordinates.push([lng, lat]);
      }
    } else if (layer instanceof L.Rectangle || layer instanceof L.Polygon) {
      const geoJSON = layer.toGeoJSON();
      coordinates = geoJSON.geometry.coordinates[0];
    } else {
      const geoJSON = layer.toGeoJSON();
      coordinates = geoJSON.geometry.coordinates[0] || geoJSON.geometry.coordinates;
    }

    console.log("Created shape coordinates:", coordinates);

    dispatch(
      addArea({
        polyId: layer._leaflet_id,
        coordinates: coordinates,
      })
    );
  };

  const _onDeleted = (e) => {
    // console.log(e);
    const deletedKeys = Object.keys(e.layers._layers);
    //console.log("deleted keys", deletedKeys);
    dispatch(deleteArea(deletedKeys));
  };

  function DeleteGeoJson() {
    const map = useMap();
    // console.log("here");
    map.eachLayer(function (layer) {
      // Skip working area boundary layer when clearing
      if ((layer instanceof L.Polygon || layer instanceof L.GeoJSON) &&
        !layer.options.className?.includes('working-area-boundary')) {
        layer.remove();
      }
    });
    // No dispatch setUploaderFlag(false) here. already done by
    // clearDrawnShapesAndMapState causing AddGeoJson to never mount.
    setDelete(false);
    dispatch(clearAllMapState());
    setGeoJsonLayerRef(null);
  }

  const markerDragHandler = {
    dragend() {
      const marker = markerRef.current;
      if (marker != null) {
        if (!isMarkerWithinArabBoundries(marker)) {
          toast.warn(t("Map.within_arab"));
          return;
        }
        //dates aren't null
        if (!datePickerTimeFrom || !datePickerTimeTo) {
          toast.warn(t("Map.select_dates"));
          return;
        }
        getWeatherData(dispatch, t, datePickerTimeFrom, datePickerTimeTo, {
          lat: markerRef.current.getLatLng().lat.toFixed(2),
          lng: markerRef.current.getLatLng().lng.toFixed(2),
        });
      }
    },
  };

  const [boundForGeoJson, setboundForGeoJson] = useState();

  function AddGeoJson() {
    const map = useMap();

    useEffect(() => {
      if (!fileContent || !uploaderFlag) return;

      // Clear existing GeoJSON layers first (but not working area boundary)
      map.eachLayer((layer) => {
        if ((layer instanceof L.GeoJSON || layer instanceof L.Polygon) &&
          !layer.options.className?.includes('working-area-boundary')) {
          map.removeLayer(layer);
        }
      });

      try {
        // Basic GeoJSON validation
        if (!fileContent || typeof fileContent !== 'object' || !fileContent.type || !Array.isArray(fileContent.features)) {
          console.error('Invalid GeoJSON structure:', fileContent);
          return;
        }

        // Create GeoJSON layer with error handling
        const geoJsonLayer = L.geoJSON(fileContent, {
          style: {
            fillOpacity: showOverlay ? 0.2 : 0, // Show fill when overlay is enabled
            fillColor: '#1976d2',
            weight: 2,
            color: '#1976d2'
          },
          onEachFeature: (feature, layer) => {
            if (feature.properties) {
              layer.bindPopup(
                Object.entries(feature.properties)
                  .map(([key, value]) => `${key}: ${value}`)
                  .join('<br>')
              );
            }
          }
        });

        if (!geoJsonLayer) {
          throw new Error('Failed to create GeoJSON layer');
        }

        // Add to map and get bounds
        geoJsonLayer.addTo(map);
        const bounds = geoJsonLayer.getBounds();

        if (bounds.isValid()) {
          setboundForGeoJson(bounds);
          map.fitBounds(bounds);
        }

        // Store the layer reference
        setGeoJsonLayerRef(geoJsonLayer);

        // Pass entire GeoJSON as ONE combined area 
        dispatch(
          addArea({
            polyId: geoJsonLayer._leaflet_id,
            coordinates: fileContent, // Entire GeoJSON object, not individual features
          })
        );
      } catch (error) {
        console.error('Error in AddGeoJson effect:', error);
        toast.error('Failed to display GeoJSON on map');
      }
    }, [map, fileContent, uploaderFlag]);

    return null;
  }

  function deleteUploadedFiles() {
    localStorage.removeItem('mapFileContent');
    dispatch(clearAllMapState());
    dispatch(setUploaderFlag(false));
    setDelete(true);
    setGeoJsonLayerRef(null);
  }

  function convertToBounds(obj) {
    const { _southWest, _northEast } = obj;

    const west = _southWest.lng;
    const east = _northEast.lng;
    const north = _northEast.lat;
    const south = _southWest.lat;

    return [
      [west, south],
      [west, north],
      [east, north],
      [east, south],
    ];
  }

  const ArabWorldBounds = {
    southWest: {
      lat: -18.454445427710418,
      lng: -18.675611088484246,
    },
    northEast: {
      lat: 45.261019159784439,
      lng: 60.756334763925977,
    },
  };
  const bounds = L.latLngBounds(
    ArabWorldBounds.southWest,
    ArabWorldBounds.northEast
  );
  const { showEditControl } = props;

  // Check if any area has an image - if so, remove GeoJSON layer
  const hasImages = areas.some(area => area.img64 || area.imgurl);

  // function getBounds(coordinates) {
  //   const southwest = [coordinates[0][1], coordinates[0][0]]; // [longitude, latitude]
  //   const northeast = [coordinates[2][1], coordinates[2][0]]; // [longitude, latitude]

  //   // Define the bounds of the shape using southwest and northeast corners
  //   return [southwest, northeast];
  // }

  // function DrawMaps() {
  //   const map = useMap();
  //   map.whenReady(() => {
  //     setTimeout(() => {
  //       map.invalidateSize();
  //     }, 0);
  //   });
  //   areas.forEach((area) => {
  //     if (area.img64) {
  //       const coords = area.coordinates.map((c) => [c[1], c[0]]);
  //       L.imageOverlay("data:image/jpeg;base64," + area.img64, coords, {
  //         interactive: true,
  //         opacity: 0.8,
  //       }).addTo(map);
  //     }
  //   });

  //   return null;
  // }

  return (
    <>
      <MapContainer
        bounds={bounds}
        maxBounds={bounds}
        center={center}
        minZoom={4}
      >
        {/* Zoom persistence component */}
        <MapStatePersistence />

        {/* FIX: Restore drawn shapes when navigating from tools */}
        <RestoreDrawnShapes />

        {/* Working area boundary layer */}
        <WorkingAreaBoundary />


        {/* Update GeoJSON layer style when showOverlay changes */}
        <UpdateOverlayStyle showOverlay={showOverlay} geoJsonLayerRef={geoJsonLayerRef} areas={areas} />

        {/* Simple fileContent check, triggers on every new upload */}
        {fileContent && uploaderFlag && !geoJsonLayerRef && <AddGeoJson />}
        {deleteState && <DeleteGeoJson />}

        <TileLayer
          attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        />

        {/* Image tile layers for areas with images */}
        {areas &&
          areas.map(
            (area, index) =>
              area.tileUrl && area.bbox && (
                <TileLayer
                  key={`${index}-${area.imagename}`}
                  url={`${url}${area.tileUrl}`}
                  bounds={area.bbox}
                />
              )
          )}

        {props.legendData && <MapLegendWrapper legends={props.legendData} />}

        {!showEditControl && (
          <Marker
            draggable={true}
            eventHandlers={markerDragHandler}
            position={markerPosition}
            ref={markerRef}
            icon={
              new Icon({
                iconUrl: markerIconPng,
                iconSize: [25, 41],
                iconAnchor: [12, 41],
              })
            }
          >
            <Popup>
              <div>
                <p>Percipitation :{currentWeather["precipitation_sum"]}</p>
                <p>Temperature: {currentWeather["temperature_2m_max"]}</p>
                <p>WindSpeed : {currentWeather["windspeed_10m_max"]}</p>
              </div>
            </Popup>
          </Marker>
        )}

        {(showEditControl && props.index) && (
          <FeatureGroup className="edit-control-container">
            <EditControl
              position="topleft"
              onCreated={(e) => {
                setIsDrawing(false);
                _onCreated(e);
              }}
              onDeleted={_onDeleted}
              onDrawStart={(e) => {
                setIsDrawing(true);
                const map = e.target;
                localStorage.removeItem('mapFileContent');
                dispatch(clearAllMapState());
                dispatch(setUploaderFlag(false));
                setDelete(true);
                setGeoJsonLayerRef(null);
                // Clear existing shapes
                map.eachLayer((layer) => {
                  // Skip working area boundary layer when clearing
                  if ((layer instanceof L.Polygon || layer instanceof L.GeoJSON) &&
                    !layer.options.className?.includes('working-area-boundary')) {
                    map.removeLayer(layer);
                  }
                });
              }}
              onDrawStop={() => {
                setIsDrawing(false);
              }}
              draw={{
                rectangle: true,
                circle: true,
                polyline: false,
                marker: false,
                polygon: true,
                circlemarker: false,
              }}
            />
          </FeatureGroup>
        )}
      </MapContainer>
      {!hideButtons && areas && areas.length > 0 && (
        <div className="remove-area" style={{ display: 'flex', gap: '10px' }}>
          <button className="delete-button" onClick={() => deleteUploadedFiles()}>
            {t("Map.remove_file")}
          </button>
          <button
            className="delete-button"
            onClick={() => dispatch(setShowOverlay(!showOverlay))}
            style={{
              backgroundColor: showOverlay ? '#329ace' : '#666',
            }}
          >
            {showOverlay ? t("Map.hide_overlay") || "Hide Overlay" : t("Map.show_overlay") || "Show Overlay"}
          </button>
        </div>
      )}
    </>
  );
}

export default Map;